import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = signed in
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatedTenant, setImpersonatedTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setImpersonating(!!data.impersonating);
      setImpersonatedTenant(data.impersonated_tenant || null);
    } catch (e) {
      setUser(false);
      setImpersonating(false);
      setImpersonatedTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const registerTenant = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register-tenant", payload);
    // Verification-required mode: NO user is returned. Caller is in charge
    // of redirecting to /check-your-email.
    if (data?.user) setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* noop */
    }
    setUser(false);
  }, []);

  const startImpersonation = useCallback(async (tenantId) => {
    const { data } = await api.post(`/admin/impersonate/${tenantId}`);
    await refresh();
    return data.tenant;
  }, [refresh]);

  const stopImpersonation = useCallback(async () => {
    try { await api.delete("/admin/impersonate"); } catch (e) { /* noop */ }
    await refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, login, logout, registerTenant,
                                     impersonating, impersonatedTenant,
                                     startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
